import React from 'react';
import { format, parseISO } from 'date-fns';
import { Receipt } from 'lucide-react';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

export default function ExpenseReportEntries({ expenses }) {
  if (!expenses || expenses.length === 0) {
    return (
      <div className="mb-3 p-4 bg-slate-50 rounded text-center text-slate-500 text-sm">
        No expenses for this period
      </div>
    );
  }

  // Calculate total
  const total = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  // Format date safely
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // Handle both ISO and simple date formats
      const date = dateStr.includes('T') ? parseISO(dateStr) : new Date(dateStr);
      return format(date, 'EEE, MMM d');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="mb-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Expense Items</p>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-100">
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Date</th>
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Job #</th>
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Category</th>
            <th className="text-left py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase">Description</th>
            <th className="text-right py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase w-20">Amount</th>
            <th className="text-center py-2 px-2 font-semibold text-slate-700 text-[10px] uppercase w-16">Receipt</th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((expense, idx) => (
            <tr key={expense.id || idx} className="border-b border-slate-100">
              <td className="py-1.5 px-2 text-slate-700 text-[11px]">
                {formatDate(expense.date)}
              </td>
              <td className="py-1.5 px-2 font-medium text-amber-600 text-[11px]">
                {expense.job_number === 'OTHER' && expense.custom_job_description
                  ? <span className="italic text-slate-500">Other: {expense.custom_job_description}</span>
                  : (expense.job_number || '—')
                }
              </td>
              <td className="py-1.5 px-2 text-slate-600 text-[11px]">
                {expense.category || '—'}
              </td>
              <td className="py-1.5 px-2 text-slate-600 text-[11px] max-w-[150px] truncate">
                {expense.description || '—'}
              </td>
              <td className="py-1.5 px-2 text-right font-medium text-slate-800 text-[11px]">
                {formatCurrency(expense.amount)}
              </td>
              <td className="py-1.5 px-2 text-center">
                {expense.receipt_filename ? (
                  <Receipt className="w-4 h-4 text-green-600 mx-auto" />
                ) : (
                  <span className="text-slate-300">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-emerald-50">
            <td colSpan={4} className="py-2 px-2 text-right font-bold text-slate-900 text-xs uppercase">Total</td>
            <td className="py-2 px-2 text-right font-bold text-emerald-700 text-sm">{formatCurrency(total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
